<script setup lang="ts">
type Props = {
  gameDetails: Record<string, any>;
  statistics: GameStatisticsByPeriod | null;
};

const { gameDetails, statistics } = defineProps<Props>();

type Emits = {
  'open-game': [Game.Mode];
};

const emit = defineEmits<Emits>();

const { openToaster } = useLayoutStore();
const { t } = useTexts();
const { openLogin } = useAuth();

type SharePlatform =
  | 'facebook'
  | 'whatsapp'
  | 'twitter'
  | 'telegram'
  | 'native';

const isSharing = ref(false);

function buildSharerUrl(platform: SharePlatform): string {
  const url = encodeURIComponent(window.location.href);
  const text = encodeURIComponent(`Venha jogar ${gameDetails.name}!`);

  switch (platform) {
    case 'whatsapp':
      return `https://api.whatsapp.com/send?text=${text}%20${url}`;

    case 'telegram':
      return `https://t.me/share/url?url=${url}&text=${text}`;

    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${url}&text=${text}`;

    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${url}`;

    default:
      return '';
  }
}

const shareGame = async (platform: SharePlatform = 'native') => {
  if (isSharing.value) return;
  isSharing.value = true;

  try {
    if (platform === 'native') {
      const shareData = {
        title: gameDetails.name + ' | ' + appName(),
        text: `Venha jogar ${gameDetails.name}!`,
        url: window.location.href,
      };

      try {
        await navigator.share(shareData);
        openToaster({
          message: t('casino.game.sharing.success'),
          type: 'success',
        });
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          openToaster({
            message: t('casino.game.sharing.not_supported'),
            type: 'error',
          });
        }
      }

      return;
    }

    const webUrl = buildSharerUrl(platform);

    if (!webUrl) return;

    window.open(webUrl, '_blank', 'noopener,noreferrer');
  } finally {
    setTimeout(() => {
      isSharing.value = false;
    }, 400);
  }
};

const shareIcons: { icon: string; platform: SharePlatform }[] = [
  { icon: 'social/facebook', platform: 'facebook' },
  { icon: 'social/whatsapp', platform: 'whatsapp' },
  { icon: 'social/x', platform: 'twitter' },
  { icon: 'social/telegram', platform: 'telegram' },
  { icon: 'share', platform: 'native' },
];

const { isDemoModeEnabled } = useFeatureFlagsNew();

const isGameDemoToggleVisible = computed<boolean>(() => {
  return !!gameDetails.demo && !!isDemoModeEnabled.value;
});
</script>

<template>
  <section :class="$style.gameLauncher">
    <div :class="$style.gameLauncherInfo">
      <div
        v-if="gameDetails.isLoading"
        :class="[`skeleton-loading h-[140px] w-[110px]`, $style.skeleton]"
      />
      <div
        v-else
        class="flex flex-col items-center bg-[#020716] text-white font-medium text-[10px] rounded-md"
      >
        <div
          class="w-[37vw]"
          style="aspect-ratio: 300/350"
        >
          <CasinoMediaApostatudo
            :game="gameDetails as unknown as Game"
            no-link
          />
        </div>
      </div>

      <div
        v-if="gameDetails.isLoading"
        class="flex-1"
      >
        <div :class="[`skeleton-loading h-5 w-full`, $style.skeleton]" />
        <div :class="[`skeleton-loading h-10 mt-2 w-full`, $style.skeleton]" />
        <div :class="[`skeleton-loading mt-2 h-3 w-8/12`, $style.skeleton]" />
      </div>

      <div
        v-else
        class="flex flex-col w-full justify-between"
      >
        <div class="flex items-center gap-2">
          <h1 :class="$style.title">
            {{ gameDetails.name }}
          </h1>
        </div>

        <p :class="$style.provider">
          {{ gameDetails.provider }}
        </p>

        <div class="flex gap-[12px] items-center mt-2 w-full">
          <CasinoVotesApostatudo :casino-game-id="gameDetails.id" />

          <div
            class="flex items-center text-texts/70 text-xs bg-transparent justify-between w-full"
          >
            <button
              v-for="item in shareIcons"
              :key="item.icon"
              class="bg-white/10 rounded-full p-1 w-[17px] h-[17px] flex items-center justify-center disabled:opacity-50"
              :disabled="isSharing"
              type="button"
              @click="shareGame(item.platform)"
            >
              <NuxtIcon
                class="opacity-80"
                :name="item.icon"
              />
            </button>
          </div>
        </div>

        <div
          class="relative w-full min-w-[100px] h-[21px] rounded-full overflow-hidden border border-red-500 mt-[16px]"
        >
          <div class="w-full h-full bg-texts/20 overflow-hidden">
            <div
              class="h-full bg-[#00C9FF] rounded-l flex items-center justify-center transition-all duration-300"
              :style="`width: ${statistics?.rtp_last_30_days}%`"
            ></div>
            <span
              class="absolute inset-0 flex items-center justify-center text-bg-primary text-[10px] font-extrabold"
            >
              RTP Médio {{ Math.round(statistics?.rtp_last_30_days || 0) }}%
            </span>
          </div>
        </div>
        <button
          v-if="!gameDetails.error_login"
          class="h-[30px] gap-[7px] rounded-[6px] px-[10px] border flex items-center justify-center font-semibold text-[12px] text-white mt-[16px]"
          style="
            background: radial-gradient(
              136.67% 136.67% at 36.76% -16.67%,
              #ff704a 0%,
              #ff3400 100%
            );
            border: 1px solid #fc9984;
          "
          @click="emit('open-game', 'real')"
        >
          <span v-if="isGameDemoToggleVisible">
            {{ $t('casino.game.real_mode') }}
          </span>
          <span v-else>JOGAR AGORA</span>
          <NuxtIcon name="play" />
        </button>
      </div>
    </div>

    <div
      v-if="gameDetails.isLoading"
      :class="$style.gameLauncherMode"
    >
      <div
        :class="[`skeleton-loading mt-2 h-[40px] w-full`, $style.skeleton]"
      />
      <div
        :class="[`skeleton-loading mt-2 h-[40px] w-full`, $style.skeleton]"
      />
    </div>

    <div
      v-else
      :class="$style.gameLauncherMode"
    >
      <button
        v-if="isGameDemoToggleVisible && !gameDetails.error_login"
        :class="[$style.btn, $style.btnDefault]"
        @click="emit('open-game', 'demo')"
      >
        <NuxtIcon name="play" />
        <b>{{ $t('casino.game.demo_mode') }}</b>
      </button>

      <button
        v-else-if="isGameDemoToggleVisible"
        :class="[$style.btn, $style.btnDefault]"
        @click="openLogin()"
      >
        <NuxtIcon name="play" />
        <b>{{ $t('casino.game.demo_mode') }}</b>
      </button>

      <button
        v-if="gameDetails.error_login"
        :class="[$style.btn, $style.btnCta]"
        @click="openLogin()"
      >
        <NuxtIcon name="play" />
        <b>{{ $t('casino.game.login') }}</b>
      </button>
    </div>
  </section>
</template>

<style module lang="postcss">
.gameLauncher {
  @apply border border-solid border-white/10 bg-bg-secondary p-[15px] mb-4 rounded-lg;

  .gameLauncherInfo {
    @apply flex flex-row items-stretch gap-3;

    .title {
      @apply text-base leading-[20px] text-white font-bold;
    }

    .provider {
      @apply text-[13px] leading-[18px] font-medium text-[#98A2B3];
    }
  }

  .gameLauncherMode {
    @apply flex justify-start gap-2;

    a {
      @apply flex-1 justify-stretch flex hover:no-underline;
    }

    .btn {
      @apply mt-2 w-full h-10 rounded flex justify-center items-center whitespace-nowrap text-sm gap-2;

      &.btnDefault {
        @apply border border-solid border-white/50 text-white/50;
      }

      &.btnCta {
        @apply bg-button-bg text-button-text;
      }

      svg {
        @apply fill-current w-4 h-4;
      }
    }
  }

  .skeleton {
    @apply bg-texts/5;
  }

  .equityButton {
    @apply bg-texts/5 -mx-5 -mb-5 mt-6 flex items-center justify-center gap-2 text-texts/30 py-2 text-xs font-semibold;

    width: calc(100% + 2.5rem);
  }

  .equityDialog {
    @apply border-none outline-none bg-bg-primary p-6 text-texts;

    margin: revert-layer;

    &::backdrop {
      @apply bg-black/70;
    }
  }

  .equityValue {
    @apply min-h-16 grid place-content-center;
  }
}
</style>
